import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { normalizeMaintenanceRequest } from "../utils/enums";
import { isPhaseAdmin } from "../utils/role";

const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try { return JSON.parse(text); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem("auth_token");
  sessionStorage.removeItem("zangon_session_active");
  try {
    const remember = localStorage.getItem("zangon_remember_me");
    if (remember !== "true") {
      localStorage.removeItem("zangon_remembered_email");
      localStorage.removeItem("zangon_remember_me");
    }
  } catch {}
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

const NAV_ITEMS = [
  { to: "/dashboard", icon: "ki-home-2", label: "Dashboard" },
  { to: "/tenants", icon: "ki-user", label: "Tenant Management" },
  { to: "/estate-access", icon: "ki-abstract-5", label: "Estate Access" },
  // { to: "/security-incidents", icon: "ki-shield-tick", label: "Security & Incidents" },
  // { to: "/nfc-cards", icon: "ki-security-check", label: "NFC Cards" },
  { to: "/car-management", icon: "ki-car", label: "Cars Management" },
  { to: "/rent-management", icon: "ki-wallet", label: "Rent Management" },
  // { to: "/payments", icon: "ki-bill", label: "Payments Ledger" },
  // { to: "/scholarship", icon: "ki-teacher", label: "Scholarship" },
  { to: "/user-management", icon: "ki-people", label: "User Management", estateOnly: true },
];

const BOTTOM_ITEMS = [
  { to: "/estate-setup", icon: "ki-setting-2", label: "Estate Setup", estateOnly: true },
  { to: "/reports", icon: "ki-graph-3", label: "Reports" },
  // { to: "/audit-log", icon: "ki-shield-search", label: "Audit Log", estateOnly: true },
  { to: "/profile", icon: "ki-profile-circle", label: "Profile" },
];

// Phase Administrators cannot reach estate-level pages (estateOnly).
const navVisible = (item) => !item.estateOnly || !isPhaseAdmin();

const MINIMIZE_KEY = "zangon_sidebar_minimized";

// Inactive menu icons sit a shade lighter than the theme default so they read on the dark sidebar.
const ICON_CSS = `
  #kt_app_sidebar .menu-link:not(.active) .menu-icon i { color: #8b90a3; }
  #kt_app_sidebar .menu-link:not(.active):hover .menu-icon i { color: #c4c7d4; }
`;

const COLLAPSIBLE_CSS = `
  @media (min-width: 992px) {
    body[data-zangon-sidebar="compact"]:not([data-kt-app-sidebar-minimize="on"]) {
      --bs-app-sidebar-width: 240px;
      --bs-app-sidebar-width-actual: 240px;
    }
    #kt_app_sidebar { top: 0; bottom: 0; height: 100vh; display: flex; flex-direction: column; }
  }
  #kt_app_sidebar .app-sidebar-navs { margin-left: 0 !important; margin-right: 0 !important; }
  #kt_app_sidebar .menu { padding: 0 0.75rem !important; }
  #kt_app_sidebar .menu-link { padding-left: 0.75rem; padding-right: 0.75rem; }
  #kt_app_sidebar .menu-link .menu-title { font-size: 14px !important; font-weight: 500 !important; }
  #kt_app_sidebar .menu-link .menu-icon { margin-right: 0.75rem; }
  #kt_app_sidebar .menu-link .menu-icon i { font-size: 1.35rem !important; }
  #kt_app_sidebar .separator { margin-left: 1.5rem !important; margin-right: 1.5rem !important; }
  #kt_app_sidebar .app-sidebar-footer { padding-left: 1.5rem !important; padding-right: 1.5rem !important; }
  #kt_app_sidebar .sidebar-logo-full { height: 38px !important; }
  #kt_app_sidebar .sidebar-icon-badge {
    position: absolute;
    top: -7px;
    right: -9px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: #f1416c;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--bs-app-sidebar-base-bg-color, #151515);
  }
  #kt_app_sidebar .app-sidebar-header,
  #kt_app_sidebar .sidebar-collapse-row,
  #kt_app_sidebar .app-sidebar-footer { flex-shrink: 0; }
  #kt_app_sidebar .app-sidebar-navs { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; scrollbar-width: none; }
  #kt_app_sidebar .app-sidebar-navs::-webkit-scrollbar { display: none; }
  #kt_app_sidebar .app-sidebar-navs > .hover-scroll-y { height: auto !important; max-height: none !important; overflow: visible !important; }

  #kt_app_sidebar .sidebar-logo-mini { display: none; }
  #kt_app_sidebar .sidebar-collapse-row { padding: 0 1.5rem 1rem; }
  #kt_app_sidebar .sidebar-collapse-btn {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.04);
    color: #9aa0b4;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  }
  #kt_app_sidebar .sidebar-collapse-btn:hover { background: #0EA84A; border-color: #0EA84A; color: #ffffff; }
  #kt_app_sidebar .sidebar-collapse-btn i { color: inherit; font-size: 1.1rem; transition: transform 0.3s ease; }

  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-logo-full { display: none; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-logo-mini { display: block; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .app-sidebar-navs { margin: 0 !important; padding: 1.25rem 0 !important; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu {
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-item { width: 100%; display: flex; justify-content: center; padding: 0 !important; margin: 0 !important; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-link {
    flex: 0 0 48px !important;
    width: 48px !important;
    height: 46px;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 12px;
    justify-content: center;
    position: relative;
    transition: background 0.2s ease;
  }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-link:hover { background: rgba(255, 255, 255, 0.06); }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-link.active { background: rgba(14, 168, 74, 0.2); }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-icon { margin: 0 !important; width: auto; justify-content: center; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-icon i { font-size: 1.55rem !important; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .menu-title { display: none; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .separator { margin: 14px 18px !important; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-collapse-row { padding: 0 0 12px; display: flex; justify-content: center; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-collapse-btn { width: 44px; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-collapse-btn i { transform: rotate(180deg); }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-collapse-label { display: none; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .app-sidebar-footer {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 12px 0 20px !important;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-signout-label { display: none; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-signout { width: 44px !important; height: 40px; padding: 0 !important; justify-content: center; }
  [data-kt-app-sidebar-minimize="on"] #kt_app_sidebar .sidebar-signout i { margin-right: 0 !important; }
  @media (max-width: 991.98px) {
    #kt_app_sidebar .sidebar-collapse-row { display: none; }
  }
`;

export default function Sidebar({ collapsible = false }) {
  const navigate = useNavigate();
  const [minimized, setMinimized] = useState(
    () => collapsible && localStorage.getItem(MINIMIZE_KEY) === "true"
  );

  useEffect(() => {
    if (!collapsible) return undefined;
    const body = document.body;
    body.setAttribute("data-zangon-sidebar", "compact");
    if (minimized) body.setAttribute("data-kt-app-sidebar-minimize", "on");
    else body.removeAttribute("data-kt-app-sidebar-minimize");
    return () => {
      body.removeAttribute("data-kt-app-sidebar-minimize");
      body.removeAttribute("data-zangon-sidebar");
    };
  }, [collapsible, minimized]);

  const toggleMinimized = () => {
    setMinimized((m) => {
      localStorage.setItem(MINIMIZE_KEY, String(!m));
      return !m;
    });
  };
  const [sessionState, setSessionState] = useState(getSession());
  const estateId = sessionState?.estateId || "";
  const accessToken = sessionState?.accessToken || "";

  const [requestCount, setRequestCount] = useState(0);
  const hasFetched = useRef(false);

  useEffect(() => {
    const handleStorage = () => setSessionState(getSession());
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (!estateId || !accessToken || hasFetched.current) return;
    hasFetched.current = true;

    fetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=1&pageSize=1000`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then(res => res.ok ? res.json() : null)
      .then(json => {
        if (!json) return;
        const open = parseArrayData(json).map(normalizeMaintenanceRequest).filter(
          r => r.status === "Created" || r.status === "Open"
        ).length;
        setRequestCount(open);
      })
      .catch(err => console.error("Failed to fetch maintenance requests:", err));
  }, [estateId, accessToken]);

  const handleLogout = (e) => {
    e.preventDefault();
    clearSession();
    navigate("/sign-in");
  };

  const iconClass = (icon, isActive) => `${isActive ? "ki-solid" : "ki-outline"} ${icon} fs-2${isActive ? " text-white" : ""}`;

  return (
    <div
      id="kt_app_sidebar"
      className="app-sidebar flex-column"
      data-kt-drawer="true"
      data-kt-drawer-name="app-sidebar"
      data-kt-drawer-activate="{default: true, lg: false}"
      data-kt-drawer-overlay="true"
      data-kt-drawer-width="330px"
      data-kt-drawer-direction="start"
      data-kt-drawer-toggle="#kt_app_sidebar_mobile_toggle"
    >
      <style>{ICON_CSS}</style>
      {collapsible && <style>{COLLAPSIBLE_CSS}</style>}
      <div className="app-sidebar-header d-none d-lg-flex justify-content-center pt-8 pb-4" id="kt_app_sidebar_header">
        <img alt="Logo" src="/assets/media/zangon-logo-white.svg" className="h-45px sidebar-logo-full" />
        {collapsible && (
          <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-40px sidebar-logo-mini" />
        )}
      </div>

      <div className="app-sidebar-navs flex-column-fluid mx-2 py-6" id="kt_app_sidebar_navs">
        <div
          className="hover-scroll-y my-2"
          data-kt-scroll="true"
          data-kt-scroll-activate="true"
          data-kt-scroll-height="auto"
          data-kt-scroll-dependencies="#kt_app_sidebar_header, #kt_app_sidebar_footer"
          data-kt-scroll-wrappers="#kt_app_sidebar_navs"
          data-kt-scroll-offset="5px"
        >
          <div className="menu menu-rounded menu-column">
            <div className="menu-item">
              <NavLink to="/tenant-requests" className="menu-link" title={minimized ? "Maintenance Request" : undefined}>
                {({ isActive }) => (
                  <>
                    <span className="menu-icon position-relative">
                      <i className={`${isActive ? "ki-solid" : "ki-outline"} ki-user-square fs-1`}></i>
                      {collapsible && requestCount > 0 && <span className="sidebar-icon-badge">{requestCount}</span>}
                    </span>
                    <span className="menu-title">Maintenance Request</span>
                    {!collapsible && requestCount > 0 && (
                      <span className="menu-badge">
                        <span className="badge badge-danger">{requestCount}</span>
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </div>
          </div>

          <div className="separator mx-10"></div>

          <div className="menu menu-column menu-rounded menu-sub-indention menu-active-bg">
            {NAV_ITEMS.filter(navVisible).map(({ to, icon, label }) => (
              <div className="menu-item" key={to}>
                <NavLink to={to} className="menu-link" title={minimized ? label : undefined}>
                  {({ isActive }) => (
                    <>
                      <span className="menu-icon"><i className={iconClass(icon, isActive)}></i></span>
                      <span className="menu-title">{label}</span>
                    </>
                  )}
                </NavLink>
              </div>
            ))}
          </div>

          <div className="separator mx-10 my-6"></div>

          <div className="menu menu-column menu-rounded menu-sub-indention menu-active-bg">
            {BOTTOM_ITEMS.filter(navVisible).map(({ to, icon, label }) => (
              <div className="menu-item" key={to}>
                <NavLink to={to} className="menu-link" title={minimized ? label : undefined}>
                  {({ isActive }) => (
                    <>
                      <span className="menu-icon"><i className={iconClass(icon, isActive)}></i></span>
                      <span className="menu-title">{label}</span>
                    </>
                  )}
                </NavLink>
              </div>
            ))}
          </div>
        </div>
      </div>

      {collapsible && (
        <div className="sidebar-collapse-row">
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleMinimized}
            title={minimized ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={minimized ? "Expand sidebar" : "Collapse sidebar"}
          >
            <i className="ki-outline ki-double-left"></i>
            <span className="sidebar-collapse-label">Collapse</span>
          </button>
        </div>
      )}

      <div className="app-sidebar-footer d-flex px-11 pb-10" id="kt_app_sidebar_footer">
        <button
          type="button"
          onClick={handleLogout}
          className="btn btn-sm btn-light-danger btn-active-danger btn-flex fw-bold w-100 h-40px px-3 align-items-center justify-content-center sidebar-signout"
          title="Sign Out"
        >
          <i className="ki-outline ki-entrance-left fs-2 me-2"></i><span className="sidebar-signout-label">Sign Out</span>
        </button>
      </div>
    </div>
  );
}